import { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import DataTable from '@/components/DataTable/DataTable';
import Button from '@/components/CustomButtons/Button';
import { useGetManagedAccounts } from '@/api/hooks/managed-accounts/useGetManagedAccounts';
import CreateManagedAccountDialog from './CreateManagedAccountDialog';
import {
  MANAGED_ACCOUNT_PROVIDER_TRANSLATION_KEY,
  MANAGED_ACCOUNT_TYPE,
  MANAGED_ACCOUNT_TYPE_TRANSLATION_KEY,
} from './managedAccount.constants';

const PageHeader = ({ title, action }) => (
  <CardHeader color='primary'>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <AccountBalanceOutlined sx={{ color: 'white', mr: 1 }} />
        <Typography
          variant='h6'
          component='h1'
          sx={{ color: 'white', fontWeight: 500 }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  </CardHeader>
);

const ManagedAccounts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const { managedAccounts, loading, error } = useGetManagedAccounts();

  const openAccount = (id) => navigate(`/admin/managed-account/${id}`);

  const translateOrFallback = (translationKeys, value) => {
    const translationKey = translationKeys[value];
    return translationKey ? t(translationKey) : value;
  };

  const columns = [
    {
      key: 'createdAt',
      label: t('managedAccounts.list.columns.createdAt'),
      format: 'date',
      sortable: false,
    },
    {
      key: 'name',
      label: t('managedAccounts.list.columns.name'),
      sortable: false,
    },
    {
      key: 'providerType',
      label: t('managedAccounts.list.columns.provider'),
      sortable: false,
      render: (value) =>
        translateOrFallback(MANAGED_ACCOUNT_PROVIDER_TRANSLATION_KEY, value),
    },
    {
      key: 'type',
      label: t('managedAccounts.list.columns.type'),
      sortable: false,
      render: (value) => (
        <Chip
          size='small'
          variant='outlined'
          color={
            value === MANAGED_ACCOUNT_TYPE.POSITIVE_PAY ? 'info' : 'primary'
          }
          label={translateOrFallback(
            MANAGED_ACCOUNT_TYPE_TRANSLATION_KEY,
            value,
          )}
        />
      ),
    },
    {
      key: 'positivePayHeaderAccountLastFour',
      label: t('managedAccounts.list.columns.headerAccountLastFour'),
      sortable: false,
      render: (value) => value ?? '–',
    },
    {
      key: 'actions',
      label: t('managedAccounts.list.columns.actions'),
      sortable: false,
      align: 'right',
      render: (_value, row) => (
        <Tooltip title={t('managedAccounts.list.actions.edit')}>
          <IconButton
            size='small'
            aria-label={t('managedAccounts.list.actions.edit')}
            onClick={(event) => {
              event.stopPropagation();
              openAccount(row.id);
            }}>
            <Edit fontSize='small' />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <PageLayout
      height='auto'
      overflow='auto'
      header={
        <PageHeader
          title={t('managedAccounts.list.title')}
          action={
            <Button
              color='white'
              size='sm'
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}>
              {t('managedAccounts.list.createAction')}
            </Button>
          }
        />
      }>
      {!loading && error && <Alert severity='error'>{error}</Alert>}

      {!error && (
        <DataTable
          columns={columns}
          testData={loading ? [] : managedAccounts}
          externalLoading={loading}
          enableColumnPicker={false}
          enableSorting={false}
          enableFilters={false}
          onRowClick={(row) => openAccount(row.id)}
        />
      )}

      <CreateManagedAccountDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          setCreateOpen(false);
          openAccount(created.id);
        }}
      />
    </PageLayout>
  );
};

export default ManagedAccounts;
